import { useEffect, useState, useRef, useMemo } from "react";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import { formatDateToISODateString, Roles, limit } from "../../../util/constant";
import { format } from "date-fns";
import { jwtDecode } from "jwt-decode";

interface DailyEntry {
  date: string;
  firstIn: string | null;
  lastOut: string | null;
  tracked: string;
  payrollHours: string;
}

interface Person {
  fullName: string;
  pictureUrl: string | null;
  timeZone: string;
  code: string;
  status: string;
}

interface TimesheetData {
  personId: string;
  total: string;
  person: Person;
  daily: DailyEntry[];
}

interface TimesheetResponse {
  value: TimesheetData[];
}

export const parseISODuration = (
  duration: string
): { hours: number; minutes: number } => {
  if (!duration || duration === "PT0S") {
    return { hours: 0, minutes: 0 };
  }

  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/;
  const matches = duration.match(regex);

  if (!matches) {
    return { hours: 0, minutes: 0 };
  }

  const hours = parseInt(matches[1] || "0", 10);
  const minutes = parseInt(matches[2] || "0", 10);
  const seconds = parseFloat(matches[3] || "0");

  const totalMinutes = minutes + Math.round(seconds / 60);

  return {
    hours: hours + Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
};

export const formatDuration = (duration: string): string => {
  const { hours, minutes } = parseISODuration(duration);
  if (hours === 0 && minutes === 0) {
    return "-";
  }
  return `${hours}:${minutes.toString().padStart(2, "0")}`;
};

export const formatTime = (timeString: string | null): string => {
  if (!timeString) return "-";

  try {
    const date = new Date(timeString);
    if (isNaN(date.getTime())) {
      return "-";
    }

    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const formattedHours = hours % 12 || 12;

    return `${formattedHours}:${minutes} ${ampm}`;
  } catch (error) {
    return "-";
  }
};

export const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

export const getWeekEnd = (date: Date): Date => {
  const weekStart = getWeekStart(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  return weekEnd;
};

export const getWeekDays = (weekStart: Date): Date[] => {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    days.push(day);
  }
  return days;
};

interface LeaveData {
  userId: {
    _id: string;
    name: string;
    email?: string;
  };
  fromDate: string;
  toDate: string;
  status: string;
  description?: string;
}

interface LeaveDateMap {
  [personId: string]: {
    [dateStr: string]: {
      description: string;
    };
  };
}

interface MonthlyTimesheetEntry {
  personId: string;
  firstName: string;
  fullName: string;
  dailyHours: { [date: string]: string };
  dailyPayrollHours: { [date: string]: string };
  dailyRestDays: { [date: string]: boolean };
  totalMonthlyHours: string;
  totalMonthlyPayrollHours: string;
}

export const useJibble = (selectedMember?: any) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [timesheetData, setTimesheetData] = useState<TimesheetData | null>(
    null
  );
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [jibbleMembers, setJibbleMembers] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<string>("");
  const [userSubRole, setUserSubRole] = useState<string>("");
  const [selectedPersonId, setSelectedPersonId] = useState<string | undefined>(
    undefined
  );
  const [monthlyTimesheetData, setMonthlyTimesheetData] = useState<
    MonthlyTimesheetEntry[]
  >([]);
  const [monthlyDataCache, setMonthlyDataCache] = useState<{
    [key: string]: MonthlyTimesheetEntry[];
  }>({});
  const [fetchProgress, setFetchProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [approvedLeaves, setApprovedLeaves] = useState<LeaveDateMap>({});
  const [employeesMap, setEmployeesMap] = useState<{
    [key: string]: { email?: string; name: string; _id: string };
  }>({});

  const isFetchingRef = useRef<boolean>(false);
  const lastFetchedMonthRef = useRef<string>("");
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  const hasInitializedRef = useRef<boolean>(false);
  const monthlyDataCacheRef = useRef<{
    [key: string]: MonthlyTimesheetEntry[];
  }>({});

  const weekStart = getWeekStart(currentWeek);
  const weekEnd = getWeekEnd(currentWeek);
  const weekDays = getWeekDays(weekStart);

  useEffect(() => {
    if (selectedMember) {
      const personId =
        selectedMember.personId || selectedMember.id || selectedMember.code;
     
      setSelectedPersonId(personId);
    } else {
      setSelectedPersonId(undefined);
    }
  }, [selectedMember]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const decodedToken: any = jwtDecode(token);
        setUserRole(decodedToken.role || "");
      } catch (error) {
        const role = localStorage.getItem("role");
        setUserRole(role || "");
      }
    } else {
      const role = localStorage.getItem("role");
      setUserRole(role || "");
    }
    const subRole = localStorage.getItem("subRole");
    setUserSubRole(subRole || "");
  }, []);

  const fetchTimesheetData = async (
    startDate: Date,
    endDate: Date,
    personId?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const date = formatDateToISODateString(startDate);
      const endDateStr = formatDateToISODateString(endDate);

      let endpoint = `${apiPaths.jibbleTimesheetsSummary}?date=${date}&endDate=${endDateStr}`;
      if (personId) {
        endpoint += `&personId=${encodeURIComponent(personId)}`;
      }


      const result: TimesheetResponse = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });


      if (result?.value && result.value.length > 0) {
        if (personId) {
          const matchingTimesheet = result.value.find(
            (ts) => ts.personId === personId
          );
          if (matchingTimesheet) {
            setTimesheetData({ ...matchingTimesheet });
          } else {
            const returnedPersonId = result.value[0]?.personId;
            const returnedPersonName = result.value[0]?.person?.fullName;
            console.error(
              "❌ BACKEND ISSUE: Requested personId:",
              personId,
              "but got:",
              returnedPersonId,
              "(",
              returnedPersonName,
              ")"
            );
            console.error(
              "⚠️ Backend is not filtering by personId parameter correctly!"
            );
            console.error(
              "Available personIds in response:",
              result.value.map((ts) => ({
                personId: ts.personId,
                name: ts.person?.fullName,
              }))
            );

            setError(
              `Backend returned data for ${returnedPersonName} instead of the selected user. Please contact support.`
            );
          }
        } else {
          setTimesheetData({ ...result.value[0] });
        }
      } else {
        console.warn("No timesheet data in response");
        setTimesheetData(null);
      }
    } catch (error: any) {
      console.error("Error fetching timesheet data:", error);
      setError(error?.message || "Failed to fetch timesheet data");
      setTimesheetData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const start = getWeekStart(currentWeek);
    const end = getWeekEnd(currentWeek);
    
    fetchTimesheetData(start, end, selectedPersonId);
  }, [currentWeek, selectedPersonId]);

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  const goToCurrentWeek = () => {
    setCurrentWeek(new Date());
  };

  const getDailyData = (date: Date): DailyEntry | null => {
    if (!timesheetData) return null;

    const dateStr = formatDateToISODateString(date);
    return timesheetData.daily.find((entry) => entry.date === dateStr) || null;
  };

  const calculateWeeklyTotal = (): { tracked: string; payroll: string } => {
    if (!timesheetData) {
      return { tracked: "-", payroll: "-" };
    }

    let totalTrackedSeconds = 0;
    let totalPayrollSeconds = 0;

    timesheetData.daily.forEach((entry) => {
      const tracked = parseISODuration(entry.tracked);
      const payroll = parseISODuration(entry.payrollHours);

      totalTrackedSeconds += tracked.hours * 3600 + tracked.minutes * 60;
      totalPayrollSeconds += payroll.hours * 3600 + payroll.minutes * 60;
    });

    const trackedHours = Math.floor(totalTrackedSeconds / 3600);
    const trackedMinutes = Math.floor((totalTrackedSeconds % 3600) / 60);
    const payrollHours = Math.floor(totalPayrollSeconds / 3600);
    const payrollMinutes = Math.floor((totalPayrollSeconds % 3600) / 60);

    return {
      tracked: `${trackedHours}:${trackedMinutes.toString().padStart(2, "0")}`,
      payroll: `${payrollHours}:${payrollMinutes.toString().padStart(2, "0")}`,
    };
  };

  const weekRange = `${format(weekStart, "MMM d")} - ${format(
    weekEnd,
    "MMM d"
  )}`;

  const fetchJibbleMembers = async () => {
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: apiPaths.jibbleMembers,
      });
      const members = Array.isArray(result) ? result : [];
      setJibbleMembers(members);
    } catch (error) {
      console.error("Error fetching jibble members:", error);
      setJibbleMembers([]);
    }
  };

  useEffect(() => {
    if (
      userRole === Roles.admin ||
      userRole === Roles.hr ||
      userSubRole === Roles.hr
    ) {
      fetchJibbleMembers();
      fetchEmployees();
    }
  }, [userRole, userSubRole]);

  const fetchEmployees = async () => {
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: apiPaths.getAllEmployees,
      });
      const employees = Array.isArray(result) ? result : [];
      const employeeMap: {
        [key: string]: { email?: string; name: string; _id: string };
      } = {};
      employees.forEach((emp: any) => {
        if (emp.email) {
          employeeMap[emp.email.toLowerCase()] = {
            email: emp.email,
            name: emp.name,
            _id: emp._id,
          };
        }
        if (emp.name) {
          employeeMap[emp.name.toLowerCase()] = {
            email: emp.email,
            name: emp.name,
            _id: emp._id,
          };
        }
      });
      setEmployeesMap(employeeMap);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployeesMap({});
    }
  };

  const fetchApprovedLeavesWithData = async (
    monthDate: Date,
    timesheetData: MonthlyTimesheetEntry[]
  ) => {
    try {
      // Fetch all pages of approved leaves
      let allLeaves: any[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const endpoint = `${apiPaths.getLeave}?status=approve&page=${page}&limit=${limit}`;
        const result = await ApiService({
          method: "GET",
          endpoint: endpoint,
        });

        const leaves = result?.leaves || result?.data || []; 
        if (Array.isArray(leaves) && leaves.length > 0) {
          // Filter to only approved leaves (in case API returns more)
          const approvedLeavesList = leaves.filter(
            (leave: any) => 
              leave.status?.toLowerCase() === "approve" || 
              leave.status?.toLowerCase() === "approved"
          );
         allLeaves = [...allLeaves, ...approvedLeavesList];
          page++;
          // If we got fewer leaves than limit, we're done
          if (leaves.length < limit) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
    
      // Process leaves and create date map
      const leaveDateMap: LeaveDateMap = {};
      const dateMap: { [dateStr: string]: Date } = {};
      const daysInMonth = getDaysInMonth(monthDate);
      daysInMonth.forEach((day) => {
        dateMap[formatDateToISODateString(day)] = day;
      });

      // Create a map of timesheet entries by name and email for quick lookup
      const timesheetByName: {
        [key: string]: MonthlyTimesheetEntry;
      } = {};
      const timesheetByPersonId: {
        [key: string]: MonthlyTimesheetEntry;
      } = {};
      
      timesheetData.forEach((entry) => {
        const nameKey = entry.fullName?.toLowerCase().trim() || "";
        const firstNameKey = entry.firstName?.toLowerCase().trim() || "";
        if (nameKey) timesheetByName[nameKey] = entry;
        if (firstNameKey) timesheetByName[firstNameKey] = entry;
        if (entry.personId) timesheetByPersonId[entry.personId] = entry;
        
        // Also add variations of the name (first word, etc.)
        if (nameKey) {
          const nameParts = nameKey.split(/\s+/);
          nameParts.forEach(part => {
            if (part.length > 2) {
              timesheetByName[part] = entry;
            }
          });
        }
      });

      
      allLeaves.forEach((leave: any) => {
        if (!leave.userId || !leave.fromDate || !leave.toDate) {
          return;
        }

        // Normalize dates - handle both ISO strings and Date objects
        const fromDateStr = leave.fromDate instanceof Date 
          ? formatDateToISODateString(leave.fromDate)
          : leave.fromDate.split("T")[0];
        const toDateStr = leave.toDate instanceof Date
          ? formatDateToISODateString(leave.toDate)
          : leave.toDate.split("T")[0];
        
        // Create dates at midnight in local timezone to avoid timezone issues
        const fromDateParts = fromDateStr.split("-");
        const toDateParts = toDateStr.split("-");
        const fromDate = new Date(
          parseInt(fromDateParts[0]),
          parseInt(fromDateParts[1]) - 1,
          parseInt(fromDateParts[2])
        );
        const toDate = new Date(
          parseInt(toDateParts[0]),
          parseInt(toDateParts[1]) - 1,
          parseInt(toDateParts[2])
        );
        
        // Get user info from leave
        const userName = leave.userId?.name || leave.userId?.fullName || "";
        const userEmail = leave.userId?.email?.toLowerCase().trim() || "";
        const userId = leave.userId?._id || leave.userId;

       
        // Check if leave overlaps with current month
        const monthStartDate = daysInMonth[0];
        const monthEndDate = daysInMonth[daysInMonth.length - 1];
        monthStartDate.setHours(0, 0, 0, 0);
        monthEndDate.setHours(23, 59, 59, 999);
        
        // Check if leave range overlaps with month range
        const leaveOverlapsMonth = fromDate <= monthEndDate && toDate >= monthStartDate;
        
        if (!leaveOverlapsMonth) {
          return;
        }

        // Find matching personId from timesheet data
        let personId: string | null = null;
        
        // Helper function to check if two names match (handles variations)
        const namesMatch = (name1: string, name2: string): boolean => {
          const n1 = name1.toLowerCase().trim();
          const n2 = name2.toLowerCase().trim();
          if (n1 === n2) return true;
          
          // Remove common suffixes and middle initials
          const cleanName1 = n1.replace(/\s*[a-z]\.?\s*/gi, " ").replace(/\s+/g, " ").trim();
          const cleanName2 = n2.replace(/\s*[a-z]\.?\s*/gi, " ").replace(/\s+/g, " ").trim();
          if (cleanName1 === cleanName2) return true;
          
          // Check if first names match (handles "Divy" vs "Divya")
          const first1 = cleanName1.split(/\s+/)[0];
          const first2 = cleanName2.split(/\s+/)[0];
          if (first1 && first2 && first1.length > 2 && first2.length > 2) {
            // Check exact match
            if (first1 === first2) return true;
            // Check if one contains the other (e.g., "divya" contains "divy")
            if (first1.includes(first2) || first2.includes(first1)) {
              // Make sure it's not just a partial substring (at least 3 chars match)
              const minLen = Math.min(first1.length, first2.length);
              if (minLen >= 3) return true;
            }
            // Check if first 3+ characters match (handles "divy" vs "divya")
            if (first1.length >= 3 && first2.length >= 3) {
              const prefix1 = first1.substring(0, 3);
              const prefix2 = first2.substring(0, 3);
              if (prefix1 === prefix2 && Math.abs(first1.length - first2.length) <= 2) {
                return true;
              }
            }
          }
          
          // Check last names match (for cases like "Patel" matching)
          const parts1 = cleanName1.split(/\s+/);
          const parts2 = cleanName2.split(/\s+/);
          if (parts1.length > 1 && parts2.length > 1) {
            const last1 = parts1[parts1.length - 1];
            const last2 = parts2[parts2.length - 1];
            if (last1 === last2 && first1 && first2 && first1.length >= 3 && first2.length >= 3) {
              // Last name matches and first names are similar enough
              if (first1.includes(first2.substring(0, 3)) || first2.includes(first1.substring(0, 3))) {
                return true;
              }
            }
          }
          
          return false;
        };
        
        // Strategy 1: Try to match by email first (most reliable)
        if (userEmail) {
          // Check if email matches any jibble member email
          const emailMatch = jibbleMembers.find((member: any) => {
            const memberEmail = (member.email || "").toLowerCase().trim();
            return memberEmail === userEmail;
          });
          
          if (emailMatch) {
            personId = emailMatch.personId || emailMatch.id || emailMatch.code || null;
            }
          
          // If found via email, try to find matching timesheet entry
          if (personId && timesheetByPersonId[personId]) {
           } else if (personId) {
            // PersonId found but not in timesheet - check if we can match by name
            const emailMatchName = emailMatch?.fullName?.toLowerCase() || emailMatch?.name?.toLowerCase() || "";
            if (emailMatchName) {
              const timesheetEntry = Object.values(timesheetData).find((entry) =>
                namesMatch(emailMatchName, entry.fullName || entry.firstName || "")
              );
              if (timesheetEntry) {
                personId = timesheetEntry.personId;
                }
            }
          }
        }
        
        // Strategy 2: Try to match by name (fuzzy matching)
        if (!personId && userName) {
          const nameKey = userName.toLowerCase().trim();
          const firstName = nameKey.split(/\s+/)[0].trim();
          
          // Try exact full name match
          if (timesheetByName[nameKey]) {
            personId = timesheetByName[nameKey].personId;
            }
          // Try exact first name match
          else if (timesheetByName[firstName] && firstName.length > 2) {
            personId = timesheetByName[firstName].personId;
            }
          // Try fuzzy matching with all timesheet entries
          else {
            const fuzzyMatch = Object.values(timesheetData).find((entry) => {
              const entryName = (entry.fullName || entry.firstName || "").toLowerCase().trim();
              return namesMatch(userName, entryName);
            });
            
            if (fuzzyMatch) {
              personId = fuzzyMatch.personId;
              }
          }
        }

        // Strategy 3: Try to match by email via employees map
        if (!personId && userEmail && employeesMap[userEmail]) {
          const employee = employeesMap[userEmail];
          const employeeName = employee.name?.toLowerCase().trim() || "";
          
          // Try to match employee name with timesheet (fuzzy)
          const employeeMatch = Object.values(timesheetData).find((entry) => {
            const entryName = (entry.fullName || entry.firstName || "").toLowerCase().trim();
            return namesMatch(employeeName, entryName);
          });
          
          if (employeeMatch) {
            personId = employeeMatch.personId;
            }
        }

        // Strategy 4: Try jibbleMembers with fuzzy matching
        if (!personId && userName) {
          const matchingMember = jibbleMembers.find((member: any) => {
            const memberName = (
              member.fullName?.toLowerCase() || member.name?.toLowerCase() || ""
            ).trim();
            return namesMatch(userName, memberName);
          });
          
          if (matchingMember) {
            const matchedPersonId = matchingMember.personId || matchingMember.id || matchingMember.code || null;
            
            // Try to find in timesheet by personId
            if (matchedPersonId && timesheetByPersonId[matchedPersonId]) {
              personId = matchedPersonId;
              } else if (matchedPersonId) {
              // Found in jibble but not in timesheet - use it anyway
              personId = matchedPersonId;
              }
          }
        }

        if (!personId) {
          console.warn(`Could not match leave user "${userName}" to any timesheet entry or jibble member`);
           return;
        }

        // Iterate through all dates in the leave range
        const currentDate = new Date(fromDate);
        currentDate.setHours(0, 0, 0, 0);
        const endDateForLoop = new Date(toDate);
        endDateForLoop.setHours(23, 59, 59, 999);
        
        let datesAdded = 0;
        while (currentDate <= endDateForLoop) {
          const dateStr = formatDateToISODateString(currentDate);
          
          // Check if this date is within the current month
          if (dateMap[dateStr]) {
            if (!leaveDateMap[personId]) {
              leaveDateMap[personId] = {};
            }
            if (!leaveDateMap[personId][dateStr]) {
              leaveDateMap[personId][dateStr] = {
                description: leave.description || "",
              };
              datesAdded++;
              } else {
              // Append description if multiple leaves on same date
              leaveDateMap[personId][dateStr].description +=
                (leaveDateMap[personId][dateStr].description ? "; " : "") +
                (leave.description || "");
            }
          }
          
          // Move to next day at midnight
          currentDate.setDate(currentDate.getDate() + 1);
          currentDate.setHours(0, 0, 0, 0);
        }
        
        if (datesAdded > 0) {
          }
      });
      setApprovedLeaves(leaveDateMap);
    } catch (error) {
      console.error("Error fetching approved leaves:", error);
      setApprovedLeaves({});
    }
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days: Date[] = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const fetchMonthlyTimesheetData = async (monthDate: Date) => {
    const cacheKey = `${monthDate.getFullYear()}-${monthDate.getMonth()}`;

    if (
      monthlyDataCacheRef.current[cacheKey] &&
      monthlyDataCacheRef.current[cacheKey].length > 0
    ) {
      setMonthlyTimesheetData(monthlyDataCacheRef.current[cacheKey]);
      setLoading(false);
      return;
    }

    if (monthlyDataCache[cacheKey] && monthlyDataCache[cacheKey].length > 0) {
      monthlyDataCacheRef.current = {
        ...monthlyDataCache,
        [cacheKey]: monthlyDataCache[cacheKey],
      };
      setMonthlyTimesheetData(monthlyDataCache[cacheKey]);
      setLoading(false);
      return;
    }

    if (isFetchingRef.current && lastFetchedMonthRef.current === cacheKey) {
      return;
    }

    if (
      fetchAbortControllerRef.current &&
      lastFetchedMonthRef.current !== cacheKey
    ) {
      fetchAbortControllerRef.current.abort();
    }

    isFetchingRef.current = true;
    lastFetchedMonthRef.current = cacheKey;
    fetchAbortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setFetchProgress({ current: 0, total: 1 });

    try {
      // Calculate start and end dates for the month
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0); // Last day of month

      const startDateStr = formatDateToISODateString(startDate);
      const endDateStr = formatDateToISODateString(endDate);
      

      const daysInMonth = getDaysInMonth(monthDate);
      const allUsersData: { [personId: string]: MonthlyTimesheetEntry } = {};
      const dateMap: { [dateStr: string]: Date } = {};
      daysInMonth.forEach((day) => {
        dateMap[formatDateToISODateString(day)] = day;
      });

      const endpoint = `${apiPaths.jibbleTimesheets}?period=Month&$count=true&$expand=person&$skip=0&$top=20&date=${startDateStr}&$orderby=person/fullName asc`;

      setFetchProgress({ current: 1, total: 1 });

      const result: any = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      let entries: any[] = [];
      if (Array.isArray(result)) {
        entries = result;
      } else if (result?.value && Array.isArray(result.value)) {
        entries = result.value;
      } else if (result?.data && Array.isArray(result.data)) {
        entries = result.data;
      }

      // Process all entries from the single API response
      entries.forEach((entry: any) => {
        const personId =
          entry.personId ||
          entry.person?.id ||
          entry.person?.code ||
          entry.id;
        if (!personId) {
          console.warn("Entry missing personId:", entry);
          return;
        }

        const fullName =
          entry.person?.fullName ||
          entry.person?.name ||
          entry.fullName ||
          entry.name ||
          "";
        const firstName = fullName.split(" ")[0] || "Unknown";

        if (!allUsersData[personId]) {
          allUsersData[personId] = {
            personId,
            firstName,
            fullName,
            dailyHours: {},
            dailyPayrollHours: {},
            dailyRestDays: {},
            totalMonthlyHours: "0:00",
            totalMonthlyPayrollHours: "0:00",
          };
        }

        // Process daily entries if available
        if (entry.daily && Array.isArray(entry.daily)) {
          entry.daily.forEach((dailyEntry: any) => {
            const dateStr = dailyEntry.date;
            if (!dateStr || !dateMap[dateStr]) return;

            // Store isRestDay information
            const isRestDay = dailyEntry.timeOff?.isRestDay || false;
            if (isRestDay) {
              allUsersData[personId].dailyRestDays[dateStr] = true;
            }

            const tracked =
              dailyEntry.trackedHours?.worked ||
              dailyEntry.trackedHours?.total ||
              dailyEntry.tracked ||
              dailyEntry.total ||
              "PT0S";
            const { hours, minutes } = parseISODuration(tracked);
            if (hours > 0 || minutes > 0) {
              allUsersData[personId].dailyHours[dateStr] = `${hours}:${minutes
                .toString()
                .padStart(2, "0")}`;
            }

            const payrollHours =
              dailyEntry.payrollHours?.total ||
              dailyEntry.payrollHours ||
              dailyEntry.payroll ||
              "PT0S";
            const { hours: payrollH, minutes: payrollM } =
              parseISODuration(payrollHours);
            // Always store payroll hours, even if 0, so they show in the table
            allUsersData[personId].dailyPayrollHours[dateStr] =
              payrollH > 0 || payrollM > 0
                ? `${payrollH}:${payrollM.toString().padStart(2, "0")}`
                : "-";
          });
        } else {
          // Fallback: if entry has a date field, use it
          const entryDate = entry.date || entry.day || entry.dateStr;
          if (entryDate && dateMap[entryDate]) {
            const tracked =
              entry.summary?.worked ||
              entry.summary?.tracked ||
              entry.tracked ||
              entry.total ||
              entry.duration ||
              entry.hours ||
              "PT0S";
            const { hours, minutes } = parseISODuration(tracked);
            if (hours > 0 || minutes > 0) {
              allUsersData[personId].dailyHours[entryDate] = `${hours}:${minutes
                .toString()
                .padStart(2, "0")}`;
            }

            const payrollHours =
              entry.summary?.payroll ||
              entry.payrollHours ||
              entry.payroll ||
              "PT0S";
            const { hours: payrollH, minutes: payrollM } =
              parseISODuration(payrollHours);
            allUsersData[personId].dailyPayrollHours[entryDate] =
              payrollH > 0 || payrollM > 0
                ? `${payrollH}:${payrollM.toString().padStart(2, "0")}`
                : "-";
          }
        }
      });

      const usersArray = Object.values(allUsersData).map((user) => {
        let totalSeconds = 0;
        Object.values(user.dailyHours).forEach((hoursStr) => {
          const match = hoursStr.match(/(\d+):(\d+)/);
          if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            totalSeconds += hours * 3600 + minutes * 60;
          }
        });

        const totalHours = Math.floor(totalSeconds / 3600);
        const totalMinutes = Math.floor((totalSeconds % 3600) / 60);

        let totalPayrollSeconds = 0;
        Object.values(user.dailyPayrollHours).forEach((hoursStr) => {
          const match = hoursStr.match(/(\d+):(\d+)/);
          if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            totalPayrollSeconds += hours * 3600 + minutes * 60;
          }
        });

        const totalPayrollHours = Math.floor(totalPayrollSeconds / 3600);
        const totalPayrollMinutes = Math.floor(
          (totalPayrollSeconds % 3600) / 60
        );

        return {
          ...user,
          totalMonthlyHours: `${totalHours}:${totalMinutes
            .toString()
            .padStart(2, "0")}`,
          totalMonthlyPayrollHours: `${totalPayrollHours}:${totalPayrollMinutes
            .toString()
            .padStart(2, "0")}`,
        };
      });

      const updatedCache = {
        ...monthlyDataCacheRef.current,
        [cacheKey]: usersArray,
      };
      monthlyDataCacheRef.current = updatedCache;
      setMonthlyDataCache(updatedCache);
      setMonthlyTimesheetData(usersArray);
      setFetchProgress(null);
      
      // Fetch approved leaves after monthly data is loaded
      if (usersArray.length > 0) {
        fetchApprovedLeavesWithData(monthDate, usersArray);
      }
    } catch (error: any) {
      if (error?.name === "AbortError") {
        return;
      }
      console.error("Error fetching monthly timesheet data:", error);
      const errorMessage =
        error?.message || "Failed to fetch monthly timesheet data";
      setError(errorMessage);
      setMonthlyTimesheetData([]);
      setFetchProgress(null);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      fetchAbortControllerRef.current = null;
    }
  };

  const goToPreviousMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentMonth(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentMonth(newDate);
  };

  const goToCurrentMonth = () => {
    setCurrentMonth(new Date());
  };

  const monthKey = useMemo(() => {
    if (!currentMonth) return "";
    return `${currentMonth.getFullYear()}-${currentMonth.getMonth()}`;
  }, [currentMonth]);

  useEffect(() => {
    const isAdminOrHr =
      userRole === Roles.admin ||
      userRole === Roles.hr ||
      userSubRole === Roles.hr;
    const hasRoles = userRole !== "" || userSubRole !== "";

    if (!hasRoles || !isAdminOrHr || !currentMonth) {
      return;
    }

    const cacheKey = `${currentMonth.getFullYear()}-${currentMonth.getMonth()}`;
    if (
      monthlyDataCacheRef.current[cacheKey] &&
      monthlyDataCacheRef.current[cacheKey].length > 0
    ) {
      setMonthlyTimesheetData(monthlyDataCacheRef.current[cacheKey]);
      hasInitializedRef.current = true;
      return;
    }

    if (monthlyDataCache[cacheKey] && monthlyDataCache[cacheKey].length > 0) {
      monthlyDataCacheRef.current = {
        ...monthlyDataCache,
        [cacheKey]: monthlyDataCache[cacheKey],
      };
      setMonthlyTimesheetData(monthlyDataCache[cacheKey]);
      hasInitializedRef.current = true;
      return;
    }

    const shouldFetch =
      monthKey &&
      monthKey !== lastFetchedMonthRef.current &&
      !isFetchingRef.current &&
      (!hasInitializedRef.current || monthKey !== lastFetchedMonthRef.current);

    if (shouldFetch) {
      hasInitializedRef.current = true;
      fetchMonthlyTimesheetData(currentMonth);
    }

    return () => {
      if (
        fetchAbortControllerRef.current &&
        lastFetchedMonthRef.current !== monthKey
      ) {
        fetchAbortControllerRef.current.abort();
        isFetchingRef.current = false;
        fetchAbortControllerRef.current = null;
      }
    };
  }, [monthKey, userRole, userSubRole]);

  // Clear approved leaves when month changes
  useEffect(() => {
    if (currentMonth) {
      setApprovedLeaves({});
    }
  }, [monthKey]);

  // Fetch approved leaves when monthlyTimesheetData and dependencies are ready
  useEffect(() => {
    const isAdminOrHr =
      userRole === Roles.admin ||
      userRole === Roles.hr ||
      userSubRole === Roles.hr;
    
    if (
      isAdminOrHr &&
      currentMonth &&
      monthlyTimesheetData.length > 0 &&
      jibbleMembers.length > 0 &&
      Object.keys(employeesMap).length > 0
    ) {
      fetchApprovedLeavesWithData(currentMonth, monthlyTimesheetData);
    }
  }, [
    monthlyTimesheetData,
    currentMonth,
    jibbleMembers,
    employeesMap,
    userRole,
    userSubRole,
  ]);

  return {
    loading,
    timesheetData,
    currentWeek,
    currentMonth,
    weekStart,
    weekEnd,
    weekDays,
    weekRange,
    error,
    jibbleMembers,
    userRole,
    userSubRole,
    monthlyTimesheetData,
    fetchProgress,
    approvedLeaves,
    getDaysInMonth,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    getDailyData,
    calculateWeeklyTotal,
    refetch: () => {
      const personId =
        selectedMember?.personId || selectedMember?.id || selectedMember?.code;
      fetchTimesheetData(weekStart, weekEnd, personId);
    },
    clearMonthlyCache: () => {
      setMonthlyDataCache({});
    },
  };
};
